import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, MapPin, Inbox, Bot, LayoutGrid, Star, ShieldCheck, Clock, Users, ArrowRight, CalendarCheck, FileText, CheckCircle2, Check, TrendingDown, TrendingUp, PhoneCall, Zap, MessageSquare, Wrench, ImageIcon, ChevronDown } from 'lucide-react';
const BOOKING_URL = 'https://api.viridiaanalytics.com/widget/booking/hv5pIfS27zbJCMMGNnkJ';
const PHONE_DISPLAY = '(623) 300-0708';
const PHONE_TEL = 'tel:+16233000708';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/dBgonnHgNocdZqWCx2rT29/VA-Logo_(1).png';
const CHAMBER_BADGE = 'https://cdn.magicpatterns.com/uploads/8HQjTNAJm388sm9my7xsCp/Scotsscommerce.png';

// GHL inbound webhook -- primed by VEGA with a sample payload (HTTP 200, field
// mapping confirmed) for exactly the keys this form sends. Inbound webhooks
// don't take an API key; the URL itself is a write-only trigger endpoint, so
// it's fine in client source (2026-08-18 handoff).
const LEAD_WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/JLycxFbUVQFY3z4zPpCS/webhook-trigger/33bbaf7b-b55d-498b-bf1b-3f24efca5231';

/* PROOF SHOTS (2026-08-18) -- the page had zero product imagery: every visual was
   an icon or a diagram, i.e. an illustration of a claim rather than evidence of
   one. These slots are the fix. Upload each screenshot in the MP editor,
   paste the CDN URL here, and the placeholder disappears. Empty string renders a
   labelled dashed box instead of a broken image, so the page is safe to publish
   before the uploads land.

   SHOT_SMS   -- the AI's reply + booking link + the customer answering "just
                 scheduled via your app". MUST be cropped to include the lead's
                 INBOUND message and its timestamp above the reply. The product is
                 speed; without both timestamps in frame the image proves nothing.
   SHOT_LEADS -- the contact list with after-hours Created timestamps (11:06pm,
                 10:54pm, 9:27am). Sits next to the 11pm story so the story reads
                 as a receipt instead of an anecdote.
   ANONYMITY: standing rule is no client names. SHOT_SMS currently shows a real
   business name -- either get written permission and use it (a recognisable local
   name beats an anonymised crop) or blur the business name AND the customer's
   name before uploading. Do not ship it half-blurred. */
const SHOT_SMS = '';
const SHOT_LEADS = '';

/* Tracking split three ways (2026-08-18) -- the old single trackLead() fired on
   every CTA click, so Meta was optimizing toward "clicked something" instead of
   a real conversion. Only the webhook-confirmed form submit is a true Lead now;
   booking and phone intent get their own distinctly-named events -- matching
   FUNNEL-SPEC.md section 5's canonical names -- so Kevin can read the funnel
   honestly in Ads Manager.

   KNOWN GAP, NOT FIXABLE HERE (2026-08-18): a COMPLETED booking happens on
   api.viridiaanalytics.com, off this origin, so this page can only ever see the
   click. BookingOpened is intent, never conversion. The real fix is in GHL --
   pixel + Lead on the booking confirmation step, ideally via Conversions API.
   Until that ships, Meta's only Lead signal is the bottom-of-page form, which is
   far too rare to optimize against. Do not "fix" this by firing Lead on the
   booking click; that is the exact bug this split was created to undo.

   Params are now carried across to the widget by buildBookingUrl() below, so the
   GHL-side work is unblocked: fire the conversion from the appointment-booked
   workflow, reading the click ID off the booking record. */
function trackWebhookLead() {
  const w = window as any;
  if (typeof w.fbq === 'function') w.fbq('track', 'Lead');
  if (typeof w.gtag === 'function') w.gtag('event', 'generate_lead');
}
function trackBookingOpened() {
  const w = window as any;
  if (typeof w.fbq === 'function') w.fbq('trackCustom', 'BookingOpened');
  if (typeof w.gtag === 'function') w.gtag('event', 'booking_click');
}
function trackCallTapped() {
  const w = window as any;
  if (typeof w.fbq === 'function') w.fbq('trackCustom', 'CallTapped');
  if (typeof w.gtag === 'function') w.gtag('event', 'call_tapped');
}

/* Attribution passthrough (2026-08-18). The booking widget is on another origin,
   so this page can only ever see the click, never the completed booking -- see
   the KNOWN GAP note above. Carrying the click ID and campaign params across the
   boundary is the half that IS fixable here: it gives GHL the fbclid/gclid it
   needs to report the booked appointment back to Meta or Google server-side,
   with the ad that caused it attached. Without this, no amount of GHL-side work
   can close the loop, because the identifier is already gone by then.
   Fails open on purpose -- any parsing error returns the plain BOOKING_URL. A
   broken booking link costs a lead; a missing utm costs a row in a report. */
const ATTRIBUTION_PARAMS = ['fbclid', 'gclid', 'wbraid', 'gbraid', 'ttclid', 'msclkid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
function buildBookingUrl(): string {
  if (typeof window === 'undefined') return BOOKING_URL;
  try {
    const incoming = new URLSearchParams(window.location.search);
    const url = new URL(BOOKING_URL);
    for (const key of ATTRIBUTION_PARAMS) {
      const value = incoming.get(key);
      if (value) url.searchParams.set(key, value);
    }
    return url.toString();
  } catch {
    return BOOKING_URL;
  }
}

/* A lead vanishing silently is worse than an error (2026-08-18 handoff). The
   visitor still sees success either way -- this is the "make it visible to
   Kevin" half: a distinct GA4 event that never fires on a real success, plus a
   local backup so the raw lead isn't lost even if nobody sees the console. */
function flagLeadDeliveryFailure(reason: string, lead: Record<string, string>) {
  console.error('[lead-form] delivery failed:', reason, lead);
  const w = window as any;
  if (typeof w.gtag === 'function') w.gtag('event', 'lead_delivery_failed', {
    reason
  });
  try {
    const key = 'va_failed_leads';
    const backup = JSON.parse(localStorage.getItem(key) || '[]');
    backup.push({
      ...lead,
      reason,
      ts: new Date().toISOString()
    });
    localStorage.setItem(key, JSON.stringify(backup));
  } catch {
    /* localStorage unavailable -- console.error above is the only record left */
  }
}
function usd(n: number) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

/* Shared scroll-reveal + reduced-motion primitives (2026-08-18, item 9/hard
   constraint: no animation library, no new dependency). Plain IntersectionObserver
   and matchMedia, a few lines each, reused wherever the page needs entrance motion
   -- the form, the case study cards, the lead-flow diagram. */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}
function useInViewOnce(threshold = 0.2) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, {
      threshold
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, threshold]);
  return [ref, inView] as const;
}

/* Screenshots are light-UI images landing on a near-black page. Dropped in raw
   they read as pasted files; framed in a dark rounded container with a hairline
   and a caption they read as embedded product. The caption is not decoration --
   it tells the visitor what to look at (the timestamps), which is the whole
   reason the shot is there. */
function ProofShot({
  src,
  alt,
  caption,
  label
}: {
  src: string;
  alt: string;
  caption: string;
  label: string;
}) {
  return <figure className="overflow-hidden rounded-xl border border-hairline2 bg-inkAlt" data-id="element-0" data-mp="2hxkqvyym63ke">
      {src ? <img src={src} alt={alt} className="block w-full" loading="lazy" data-id="element-1" data-mp="2d5136967ps1v" /> : <div className="flex min-h-[180px] flex-col items-center justify-center gap-2 border-b border-dashed border-hairline2 bg-ink px-6 py-10 text-center" data-id="element-2" data-mp="285ws1czq9k8w">
          <ImageIcon className="h-5 w-5 text-neutral-600" strokeWidth={2} data-id="element-3" data-mp="235sftz5urboh" />
          <div className="text-xs font-semibold text-neutral-500" data-id="element-4" data-mp="1y7qt9497wu89">Screenshot slot: {label}</div>
          <div className="text-[11px] leading-snug text-neutral-600" data-id="element-5" data-mp="1u7qgdo1ror3b">Upload in the editor, paste the URL into the constant at the top of App.tsx.</div>
        </div>}
      <figcaption className="px-5 py-4 text-xs leading-relaxed text-neutral-400" data-id="element-6" data-mp="dpgdllmxlmbb">{caption}</figcaption>
    </figure>;
}

/* CTA variants (2026-08-18): the hero used to make Book the filled primary and
   Call the outline secondary. That is backwards for this buyer -- a contractor
   reaches for the phone before a calendar link, and the call IS the demo, since
   the number rings the GHL line and the AI picks up. So the hero now leads with
   Call. Everything below the fold keeps Book-primary on purpose: by then the
   visitor has read the argument and booking is the higher-value action. One
   change at a time so the hero test stays readable.

   BOOK_LABEL: the page previously used four different Book labels ("Book Your
   Free 15-Minute Call", "Book a 15-Min Call", "Get this on your calendar",
   "Book Your Call"). Four labels is four decisions for the reader. One constant,
   used everywhere; override only with a deliberate reason. */
const BOOK_LABEL = 'Book a 15-Minute Call';
function BookCTA({
  label = BOOK_LABEL,
  full = false,
  variant = 'primary'
}: {
  label?: string;
  full?: boolean;
  variant?: 'primary' | 'secondary';
}) {
  const base = 'group inline-flex items-center justify-center gap-2 rounded-md px-7 py-4 text-sm font-bold transition-colors';
  const skin = variant === 'primary' ? 'bg-emerald-500 text-black hover:bg-emerald-400' : 'border border-emerald-500/60 bg-transparent text-emerald-300 hover:bg-emerald-500/10';
  const bookingHref = React.useMemo(() => buildBookingUrl(), []);
  return <a href={bookingHref} onClick={trackBookingOpened} className={`${base} ${skin} ${full ? 'w-full' : ''}`} data-id="element-7" data-mp="26cywbuhbyfrg">
      {label}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-8" data-mp="1dqe3epl7hvls" />
    </a>;
}

/* Calling is the lower-friction ask for this audience -- contractors reach for the
   phone before they fill in a form. The number is the GHL line, so the AI picks up:
   the call itself is a live demo of the product. Tap intent is real but isn't a
   confirmed lead, so it fires its own event, not Lead -- name per FUNNEL-SPEC.md
   section 5 (2026-08-18). */
function CallCTA({
  full = false,
  variant = 'secondary'
}: {
  full?: boolean;
  variant?: 'primary' | 'secondary';
}) {
  const base = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-4 text-sm font-bold tracking-tight transition-colors';
  const skin = variant === 'primary' ? 'bg-emerald-500 text-black hover:bg-emerald-400' : 'border border-emerald-500/60 bg-transparent text-emerald-300 hover:bg-emerald-500/10';
  return <a href={PHONE_TEL} onClick={trackCallTapped} className={`${base} ${skin} ${full ? 'w-full' : ''}`} data-id="element-9" data-mp="2zajbaoqsr1x9">
      <PhoneCall className="h-4 w-4 flex-none" data-id="element-10" data-mp="2u9n6m8ymfbqs" />
      Call {PHONE_DISPLAY}
    </a>;
}
function Eyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="font-eyebrow inline-block text-[11px] font-semibold uppercase text-emerald-500" data-id="element-11" data-mp="2fmwt897mhsou">{children}</div>;
}
function GuaranteeBadge({
  big = false
}: {
  big?: boolean;
}) {
  return <div className={`inline-flex items-center gap-2 rounded-full border-2 border-emerald-500/60 bg-emerald-500/15 font-extrabold uppercase tracking-wide text-emerald-300 shadow-[0_0_40px_-10px_rgba(16,185,129,0.7)] ${big ? 'px-6 py-3 text-base' : 'px-5 py-2.5 text-sm'}`} data-id="element-12" data-mp="26n9r1l72kiuq">
      <ShieldCheck className={big ? 'h-6 w-6' : 'h-5 w-5'} data-id="element-13" data-mp="2yga711j7fih8" /> 14-Day Money-Back Guarantee
    </div>;
}

/* Moved OUT of the hero (2026-08-12). It was the first thing a visitor met on
   mobile -- two empty inputs asking them to do math before they knew what this
   was. 125 landing views produced zero CTA clicks with it gating the top of the
   page. It stays as a tool for people who scroll, not as the entry ask. */
function ProofStats() {
  const stats = [{
    icon: Inbox,
    stat: '< 1 in 6',
    body: 'unanswered calls to a contractor ever becomes a voicemail'
  }, {
    icon: Users,
    stat: '85%',
    body: 'of homeowners contact three or fewer contractors before hiring'
  }, {
    icon: TrendingDown,
    stat: '~40%',
    body: 'say they rarely or never hire a contractor who missed their first call'
  }];
  return <div className="mt-8 rounded-xl border border-hairline2 bg-ink p-6 sm:p-8" data-id="element-14" data-mp="1ffjclq0wd8uz">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 sm:gap-4" data-id="element-15" data-mp="1fhm125meitxl">
        {stats.map(({
        icon: Icon,
        stat,
        body
      }) => <div key={body} className="flex items-start gap-3 sm:flex-col sm:items-start sm:gap-2" data-id="element-16" data-mp="mr3zj91bt0ma">
            <Icon className="mt-0.5 h-4 w-4 flex-none text-emerald-500 sm:mt-0" strokeWidth={2} data-id="element-17" data-mp="11oe8aaml3seb" />
            <div data-id="element-18" data-mp="wp1pc0yu35h0">
              <div className="text-2xl font-extrabold text-white" data-id="element-19" data-mp="woicgyo1j79u">{stat}</div>
              <p className="mt-0.5 text-xs leading-snug text-neutral-400" data-id="element-20" data-mp="2d2rvdd9ik3ta">{body}</p>
            </div>
          </div>)}
      </div>
      <p className="mt-5 border-t border-hairline2 pt-4 text-[11px] leading-relaxed text-neutral-500" data-id="element-21" data-mp="2835fsx67qbhb">
        Source: Service Direct's 2022 survey of 559 homeowners, and Service Direct's 2022 Call Performance Report.
        A homeowner picks from a short list and decides fast. Answering the phone isn't customer service, it's admission to that list.
      </p>
    </div>;
}

/* The low-commitment third door (2026-08-18 handoff): book and call are both
   high commitment; this is the raised hand for everyone not ready to talk to a
   human. MOVED to the bottom of the descent later the same day (Kevin's
   sequencing pass) -- it originally sat right after the pain section, asking
   for contact info before the visitor had seen the fix, the proof, or the
   price. Down there it's the safety net after the offer + guarantee, not a
   speed bump mid-argument. Same form, same webhook, nothing else changed.
   Full-width card, larger inputs, glow treatment, once-only scroll reveal. */
function SendNumbersForm() {
  const [name, setName] = useState('');
  const [business, setBusiness] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done'>('idle');
  const [ref, inView] = useInViewOnce(0.25);
  const reducedMotion = usePrefersReducedMotion();
  const revealed = reducedMotion || inView;
  const bookingHref = React.useMemo(() => buildBookingUrl(), []);
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;
    const lead = {
      name,
      business,
      phone,
      email,
      source: 'ai.viridiaanalytics.com missed-lead check',
      page: typeof window !== 'undefined' ? window.location.href : '',
      hp
    };

    // Honeypot: a human never fills this field. If it's non-empty, drop the
    // submit client-side without hitting the webhook -- the URL is public.
    if (hp) {
      setStatus('done');
      return;
    }
    setStatus('submitting');
    try {
      const res = await fetch(LEAD_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(lead)
      });
      if (res.ok) {
        trackWebhookLead();
      } else {
        flagLeadDeliveryFailure('HTTP ' + res.status, lead);
      }
    } catch (err) {
      flagLeadDeliveryFailure(err instanceof Error ? err.message : 'network error', lead);
    }
    // Visitor always sees the success state -- a failed delivery is Kevin's
    // problem to chase via the flagged event/backup, not the visitor's to retry.
    setStatus('done');
  }
  const cardBase = `relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-inkAlt p-8 shadow-[0_20px_60px_-30px_rgba(16,185,129,0.4)] transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] sm:p-10 ${revealed ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`;
  if (status === 'done') {
    return <div ref={ref} className={cardBase} data-id="element-22" data-mp="3ke52bpi3fzf8">
        <div className="pointer-events-none absolute inset-0 opacity-70" style={{
        background: 'radial-gradient(60% 80% at 50% 0%, rgba(15,176,125,0.10) 0%, rgba(10,10,10,0) 70%)'
      }} data-id="element-23" data-mp="3kf6g41qhv00n" />
        <div className="relative text-center" data-id="element-24" data-mp="3u59gwo3uxi9j">
          <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-400" data-id="element-25" data-mp="3u612d63qpzm3" />
          <h2 className="mt-4 text-2xl font-bold text-white" data-id="element-26" data-mp="3pbwv0lqkbpkh">Got it, we'll call you.</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-neutral-300" data-id="element-27" data-mp="6y73df3anj5b">
            Kevin will call you within one business day with a straight read on what missed calls are costing your shop.
          </p>
          <a href={bookingHref} onClick={trackBookingOpened} className="mt-5 inline-block text-xs font-semibold text-emerald-400 underline decoration-emerald-700 underline-offset-4 hover:text-emerald-300" data-id="element-28" data-mp="gwwkh5nd8zki">
            Or grab a time now instead
          </a>
        </div>
      </div>;
  }
  return <div ref={ref} className={cardBase} data-id="element-29" data-mp="2ds2ju30unxz5">
      <div className="pointer-events-none absolute inset-0 opacity-70" style={{
      background: 'radial-gradient(60% 80% at 50% 0%, rgba(15,176,125,0.10) 0%, rgba(10,10,10,0) 70%)'
    }} data-id="element-30" data-mp="1rxc0zrsv82wk" />
      <div className="relative" data-id="element-31" data-mp="21mlvtff3fzod">
        <div className="font-eyebrow text-[10px] font-semibold uppercase text-emerald-500" data-id="element-32" data-mp="21nn5pgkdig4i">Not ready to call?</div>
        <h2 className="mt-2 text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-33" data-mp="26l5ftaa4n1e3">Send me my numbers, I'll call you.</h2>
        <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-34" data-mp="19013abmvthj8">
          Three fields. Kevin calls you back within one business day with a straight read on what missed calls are costing your shop.
        </p>
        <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 gap-4" data-id="element-35" data-mp="1nv78iwe5lcbi">
          <input type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Your name" aria-label="Your name" autoComplete="name" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-36" data-mp="1nt47zifdk0sj" />
          <input type="text" required value={business} onChange={e => setBusiness(e.target.value)} placeholder="Business name" aria-label="Business name" autoComplete="organization" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-37" data-mp="inxly8f66qzg" />
          <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number" aria-label="Phone number" autoComplete="tel" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-38" data-mp="402qwldowaj7" />
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email (optional)" aria-label="Email (optional)" autoComplete="email" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-39" data-mp="3m6h6eorlwsjp" />
          {/* Honeypot -- visually hidden from sighted users, off the tab order, still
              reachable by a bot filling every field it finds. */}
          <input type="text" name="hp" value={hp} onChange={e => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }} data-id="element-40" data-mp="22g84bjb9axi9" />
          <button type="submit" disabled={status === 'submitting'} className="mt-1 w-full rounded-lg bg-emerald-500 px-7 py-4 text-base font-bold text-black transition-colors hover:bg-emerald-400 disabled:opacity-60" data-id="element-41" data-mp="1jiouidgz5aus">
            {status === 'submitting' ? 'Sending…' : 'Send my numbers'}
          </button>
        </form>
        <p className="mt-4 text-center text-xs text-neutral-500" data-id="element-42" data-mp="38x3ao9dxfpeo">No spam, no drip campaign. Kevin calls you himself.</p>
      </div>
    </div>;
}

/* ONBOARDING (new, 2026-08-18, Kevin: "maybe there needs to be an easy how it
   works when signing up type of section"). The page already explained how a LEAD
   flows (section 3) but never how ONBOARDING works. Those are different
   questions, and the second one is the last objection standing after price and
   guarantee: how much of my time does this eat and will I have to learn
   software. Kevin already knows this objection is real -- roughly half his
   existing clients never log into the CRM.

   Design rules, do not break them on edit:
     - The client-side list is short, specific, time-stamped -- and COMPLETE.
       Do not add tasks to it, and do not remove real ones. "You touch nothing"
       was false (corrected 2026-08-19, Kevin: "they do have to complete a couple
       steps by themselves... they have to sync their calendar, log into their
       socials for reviews"). A false promise here voids trust in the guarantee
       directly above it, whose terms condition on these exact steps being done.
       Half an hour of their time, itemised, is more credible than zero.
     - No features here. Effort and timeline only. The feature grid is section 3.
     - Every step carries a time.
     - Step 1 names the calendar sync, but always with the walkthrough and the
       five-minute figure attached (updated 2026-08-19). Bare "you link your
       Gmail/calendar" makes a 52-year-old roofer hear "I have to set up an
       integration" and close the tab; "we walk you through it, five minutes" is
       the same truth without the dread.
     - Step 3 deliberately does NOT say "you go back to work" (Kevin's own
       correction) -- it implies he is currently away from work.
     - Ad optimization is an UPSELL, not part of the $1,197, so it is kept out of
       step 3 on purpose. The section that promised it as if included was removed
       2026-08-18; see the note where it used to sit, above THE DARE.

   TIMELINE: five days, Kevin's number, chosen deliberately conservative so he
   can still hit it in a busy week. If that ever changes, change DAYS_TO_LIVE and
   the step-2 label together. */
const DAYS_TO_LIVE = 5;
function Onboarding() {
  /* The control objection ("will this book someone into my Saturday, or a job
     forty miles out") is real and stays answered -- but in step 2's copy, not in
     a screenshot of the booking calendar (removed 2026-08-18). A customer-facing
     calendar shows the RESULT of those settings, never the settings, so it read
     as "here is a calendar" and proved nothing. This is an easy claim to
     believe; screenshots are expensive and belong on the hard ones. */
  const steps = [{
    when: 'Day 1, 20 minutes',
    title: 'One short call.',
    body: 'We connect your phone and your Yelp, and you sync your calendar while we walk you through it, about five minutes. We also collect your business info for carrier text registration, which is required before any business can send automated texts. You talk, we type.'
  }, {
    when: `Days 2 to ${DAYS_TO_LIVE}, we build`,
    title: 'We write it in your voice.',
    body: 'Auto-responders, lead form, and AI receptionist, built around how your shop actually answers people, and we file your carrier registration. You look the wording over once and tell us if anything sounds off. You set your hours and your service area, and it never books outside them.'
  }, {
    when: `Day ${DAYS_TO_LIVE} onward`,
    title: 'Nothing changes on your end.',
    body: 'Every lead answered in seconds. Estimates on your calendar before you wake up. Invoices and review requests handled in the background. No new app to check, no new system to learn.'
  }];
  return <section className="border-b border-hairline bg-inkAlt" data-id="element-43" data-mp="3jiza013yrowa">
      <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-44" data-mp="3jge5t5g70wvd">
        <Eyebrow data-id="element-45" data-mp="3fgcffsfwrpve">What happens after you say yes</Eyebrow>
        <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-46" data-mp="1uzcznyjy55ka">
          You are live in {DAYS_TO_LIVE} days. Your part takes about half an hour.
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-47" data-mp="23zrmx3gh0uqf">
          The most common thing we hear is "I don't have time to learn another piece of software." You don't have to.
          Here is the entire process, start to finish.
        </p>

        <div className="mt-9 grid grid-cols-1 gap-4 lg:grid-cols-3" data-id="element-48" data-mp="2dpb6ycfx6umv">
          {steps.map((s, i) => <div key={s.title} className="relative rounded-lg border border-hairline2 bg-ink p-6 sm:p-7" data-id="element-49" data-mp="2iqs54070s2zr" data-mpi={i}>
              <div className="flex items-center gap-3" data-id="element-50" data-mp="2mo6epsym4csa">
                <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full border border-emerald-500/50 bg-emerald-500/10 text-sm font-extrabold text-emerald-400" data-id="element-51" data-mp="2roauthvlz5hz">
                  {i + 1}
                </div>
                <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-52" data-mp="xdf9rg38lxyb">{s.when}</div>
              </div>
              <h3 className="mt-4 text-base font-semibold text-white" data-id="element-53" data-mp="1vnf4f73ovduo">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-54" data-mp="1vpi2meezwdah">{s.body}</p>
            </div>)}
        </div>

        <div className="mt-8" data-id="element-55" data-mp="24nbxdkx42yvj">
          <div className="rounded-lg border border-emerald-500/25 bg-ink p-6 sm:p-7" data-id="element-56" data-mp="24mackoqamy0m">
            <Eyebrow data-id="element-57" data-mp="2jjtaohov2nkq">What we need from you</Eyebrow>
            <ul className="mt-4 space-y-2.5" data-id="element-58" data-mp="2em18muui5l2t">
              {['Twenty minutes on one call', 'Sync your calendar, about five minutes, we walk you through it', 'Log into your review platforms once so review requests can go out, about five minutes', 'One look at the wording before we turn it on'].map(li => <li key={li} className="flex items-start gap-2 text-sm leading-relaxed text-neutral-300" data-id="element-59" data-mp="3299af2sq2c03">
                  <Check className="mt-0.5 h-4 w-4 flex-none text-emerald-500" strokeWidth={2.5} data-id="element-60" data-mp="3gyb0g66ss7v5" />
                  {li}
                </li>)}
            </ul>
            <p className="mt-5 border-t border-hairline2 pt-4 text-sm leading-relaxed text-neutral-400" data-id="element-61" data-mp="3lvb82ljapiod">
              That is the whole list, about half an hour total. Everything else is on us, including the parts other companies hand you a login for and call onboarding.
            </p>
          </div>
        </div>

        <div className="mt-9 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-62" data-mp="2y5yrv1pjwack">
          <BookCTA data-id="element-63" data-mp="330cqre3f8zlz" />
          <CallCTA data-id="element-64" data-mp="2o9voxtmvk7ok" />
        </div>
      </div>
    </section>;
}

/* FAQ (2026-08-19, Kevin: "there should be FAQs, this shortens long text boxes
   and allows us to frame the simple terms"). Rules for editing this section:
   - Questions stay in the buyer's voice, including the hostile ones. An FAQ
     that only asks softballs reads as marketing in a disguise.
   - It FRAMES the terms, it never replaces or contradicts them. Every answer
     here must stay consistent with the terms section and the guarantee. If
     terms change, re-check every answer.
   - The go-live answer is deliberately honest about carrier registration
     being outside our control -- that honesty is doing sales work: it makes
     the 5-minute guarantee claim more believable by contrast.
   - Q1's answer assumes the AI discloses it is an AI when asked directly.
     If that ever changes in the receptionist config, this answer is false.
   - Q4 deliberately contains NO client-behavior stats. "Plenty of clients never
     log in" was removed 2026-08-19 as internal info (Kevin). Rule going forward:
     facts about how existing clients behave are internal by default and only go
     on the page with Kevin's explicit sign-off. */
const FAQS = [{
  q: "Will my customers know they're talking to a robot?",
  a: "If they ask, it tells them the truth. Most don't ask, because it answers like a sharp office manager, not a phone tree. Don't take our word for it: the number on this page is the same system. Call it and judge for yourself."
}, {
  q: "What's the catch on the guarantee?",
  a: "There isn't one, but there is a scope: we guarantee the thing we fully control. Every missed call gets a response within 5 minutes during your first 14 days, every response is timestamped and logged, and if we miss one, your first month is refunded. We don't guarantee revenue or booked jobs, because anyone who does is lying to you. The full terms are at the bottom of this page, and they're four paragraphs, not forty."
}, {
  q: "Will it book jobs I don't want, or times I don't work?",
  a: 'No. You set your hours and your service area during setup, and it never books outside them. Your calendar stays yours.'
}, {
  q: 'Do I have to learn new software?',
  a: "No. When a lead books, you get a text with the job on your calendar. There's a dashboard and a mobile app if you ever want them, but nothing about the system requires you to log in. It runs whether you look at it or not."
}, {
  q: 'How fast am I actually live?',
  a: "Your part is about half an hour, and most shops are live in 5 days. One honest caveat: before any business can send automated texts, phone carriers have to approve a registration. We file it on day one and it usually clears fast, but that approval is the carriers' clock, not ours, and your 14-day guarantee doesn't start until you're actually live."
}, {
  q: 'What if I want out?',
  a: "You leave. Month to month, no contract, 7 days' notice. If we're not catching enough missed work to obviously pay for ourselves, you shouldn't stay, and we'd rather you leave than resent the invoice."
}];
function FAQ() {
  const [open, setOpen] = useState<number | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  return <section className="border-b border-hairline bg-inkAlt" data-id="element-65" data-mp="1k9wfa0426st3">
      <div className="mx-auto max-w-3xl px-5 py-14 sm:py-20" data-id="element-66" data-mp="1kb7fxr27jkfz">
        <Eyebrow data-id="element-67" data-mp="10oe1i3mp4klh">Straight answers</Eyebrow>
        <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-68" data-mp="10pfl4whs2fet">
          The questions you're already thinking.
        </h2>

        <div className="mt-9 space-y-3" data-id="element-69" data-mp="19p2hh3vngv10">
          {FAQS.map(({
          q,
          a
        }, i) => {
          const isOpen = open === i;
          const panelId = `faq-panel-${i}`;
          const buttonId = `faq-button-${i}`;
          return <div key={q} className={`overflow-hidden rounded-lg border bg-ink transition-colors ${isOpen ? 'border-emerald-500/40' : 'border-hairline2'}`} data-id="element-70" data-mp="1jk467ym0jv32" data-mpi={i}>
                <h3 data-id="element-71" data-mp="1tayt95732ypk">
                  <button type="button" id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpen(isOpen ? null : i)} className="flex min-h-[44px] w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-emerald-500/[0.04]" data-id="element-72" data-mp="1tecta6s9yvtu">
                    <span className={`text-sm font-semibold leading-snug sm:text-[15px] ${isOpen ? 'text-white' : 'text-neutral-200'}`} data-id="element-73" data-mp="zzjwv82bpsf0">{q}</span>
                    <ChevronDown className={`h-4 w-4 flex-none ${isOpen ? 'rotate-180 text-emerald-400' : 'text-neutral-500'} ${reducedMotion ? '' : 'transition-transform duration-300'}`} strokeWidth={2.5} aria-hidden="true" data-id="element-74" data-mp="140c6i2f3xzbt" />
                  </button>
                </h3>
                <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen} className={reducedMotion ? 'grid' : `grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`} data-id="element-75" data-mp="1npe85l6zv8x5">
                  <div className="overflow-hidden" data-id="element-76" data-mp="3lzlohw90lz23">
                    <p className="px-5 pb-5 text-sm leading-relaxed text-neutral-400" data-id="element-77" data-mp="3h7txxlpuztby">{a}</p>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}

/* Case study section (item 6/7, 2026-08-18 handoff): the same two verified
   results the proof section used to carry as short blurbs, restructured as a
   story -- situation, what changed, the number -- because a story reads as
   evidence and a bare percentage reads as a claim. No client names, no
   screenshots, both per Kevin's standing anonymity rule.
   NOTE (2026-08-18, later same day): this section originally REPLACED the dare
   section. That was reversed -- Kevin confirmed the receptionist is reliable
   enough to hand to a cold stranger, so the dare is back above and these two now
   run together: the dare proves the product live, the case studies prove the
   outcome. Do not re-cut the dare without also cutting the hero's promise of it. */
function CaseStudyCard({
  eyebrow,
  situation,
  change,
  number
}: {
  eyebrow: string;
  situation: string;
  change: string;
  number: string;
}) {
  return <div className="rounded-lg border border-hairline2 bg-inkAlt p-6 sm:p-7" data-id="element-78" data-mp="3ufarohoiia57">
      <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-79" data-mp="2affd0oz6jaq">{eyebrow}</div>
      <div className="mt-5 space-y-4" data-id="element-80" data-mp="pzrq44zyazf5">
        <div className="flex gap-3" data-id="element-81" data-mp="q0sz83xepbu8">
          <Wrench className="mt-0.5 h-4 w-4 flex-none text-neutral-500" strokeWidth={2} data-id="element-82" data-mp="uze107sb43ks" />
          <p className="text-sm leading-relaxed text-neutral-400" data-id="element-83" data-mp="ux07i3d9yzfc"><span className="font-semibold text-neutral-300" data-id="element-84" data-mp="gytilcqb09rt">The situation. </span>{situation}</p>
        </div>
        <div className="flex gap-3" data-id="element-85" data-mp="lx4sfnglmka1">
          <Zap className="mt-0.5 h-4 w-4 flex-none text-emerald-500" strokeWidth={2} data-id="element-86" data-mp="m0it8r9m6lfd" />
          <p className="text-sm leading-relaxed text-neutral-400" data-id="element-87" data-mp="31rx0z1beqatj"><span className="font-semibold text-neutral-300" data-id="element-88" data-mp="36pygxddjdu0s">What changed. </span>{change}</p>
        </div>
        <div className="flex gap-3" data-id="element-89" data-mp="2y6v3npmf6p9c">
          <TrendingUp className="mt-0.5 h-4 w-4 flex-none text-emerald-500" strokeWidth={2} data-id="element-90" data-mp="2y9zg1aa1grb3" />
          <p className="text-sm leading-relaxed text-white" data-id="element-91" data-mp="2u8dzneyapuw5"><span className="font-semibold" data-id="element-92" data-mp="386cawbhblb7f">The number. </span>{number}</p>
        </div>
      </div>
    </div>;
}
function CaseStudies() {
  const [ref, inView] = useInViewOnce(0.15);
  const reducedMotion = usePrefersReducedMotion();
  const revealed = reducedMotion || inView;
  const cards = [{
    eyebrow: 'Phoenix landscaping company',
    situation: 'A two-truck crew was missing about 1 in 4 calls, business as usual when there is more work than hands to answer the phone.',
    change: 'The AI answered every call and Yelp message instantly, day and night, and booked estimates straight onto the calendar without anyone touching a phone.',
    /* "39.95%" rounded to "about 40%" (2026-08-18). Odd precision on a
       self-reported number reads as invented, not rigorous. */
    number: '284% more leads on the same ad spend, 28 estimates booked end to end by the AI, and a close rate around 40%. Response time went from 4+ hours to instant.'
  }, {
    eyebrow: 'Residential and commercial painting company',
    situation: 'Close rate was strong whenever they actually reached a lead. The problem was reaching them before someone else did.',
    change: 'The AI took first response off their plate and booked its own estimates while the owner stayed on the tools running the jobs he already had.',
    number: '29 estimates booked in 30 days, about one a day, and $15,050 closed from estimates the AI booked hands-free.'
  }];
  return <section className="border-b border-hairline bg-inkAlt" data-id="element-93" data-mp="3mviinbgtdvyu">
      <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-94" data-mp="3mswc9f5ntmi7">
        <Eyebrow data-id="element-95" data-mp="x4vnysikps7t">What this looks like for a shop like yours</Eyebrow>
        <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-96" data-mp="1248m49ewalpp">
          Two real shops, before and after.
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-97" data-mp="1c7x3nk7e7qzj">
          Real past-client outcomes, anonymized. Not a guarantee of the same numbers for every business.
        </p>
        <div ref={ref} className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-98" data-mp="12h2hefu5cre3">
          {cards.map((c, i) => <div key={c.eyebrow} className="transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]" style={{
          transitionDelay: revealed ? `${i * 150}ms` : '0ms',
          opacity: revealed ? 1 : 0,
          transform: revealed ? 'translateY(0)' : 'translateY(24px)'
        }} data-id="element-99" data-mp="yf7gvdk1ii1d" data-mpi={i}>
              <CaseStudyCard {...c} data-id="element-100" data-mp="fk0mad44srn9" />
            </div>)}
        </div>
      </div>
    </section>;
}
const FLOW_SOURCES = ['Missed call', 'Yelp', 'Google', 'Facebook', 'Web form'];
const FLOW_STAGES = [{
  icon: Inbox,
  label: 'Lead captured',
  sub: 'Every call & message'
}, {
  icon: CalendarCheck,
  label: 'Booked',
  sub: 'On your calendar'
}, {
  icon: FileText,
  label: 'Invoice sent',
  sub: 'QuickBooks synced'
}, {
  icon: CheckCircle2,
  label: 'Job completed',
  sub: 'Tracked to done'
}, {
  icon: Star,
  label: 'Review requested',
  sub: 'Your reputation grows'
}];

/* Depth on the nodes (item 5, 2026-08-18): a soft directional glow + inset
   highlight instead of a flat fill, real easing instead of a linear transition. */
function FlowNode({
  stage,
  state
}: {
  stage: (typeof FLOW_STAGES)[number];
  state: 'idle' | 'current' | 'done';
}) {
  const Icon = stage.icon;
  const done = state === 'done';
  const current = state === 'current';
  return <div className="flex w-28 flex-none flex-col items-center text-center sm:w-32" data-id="element-101" data-mp="25euljzush80w">
      <div className="relative" data-id="element-102" data-mp="3uuu3a5ch45s5">
        {current && <span className="absolute inset-0 rounded-full bg-emerald-500/30 animate-[vaPing_1.6s_ease-out_infinite]" data-id="element-103" data-mp="3uv3tnu1fkeyd" />}
        <div className={`relative flex h-12 w-12 items-center justify-center rounded-full border transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${done ? 'border-emerald-500 bg-gradient-to-b from-emerald-400 to-emerald-600 text-black shadow-[0_4px_16px_-2px_rgba(16,185,129,0.55),inset_0_1px_0_rgba(255,255,255,0.3)]' : current ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400 shadow-[0_0_0_5px_rgba(16,185,129,0.08)]' : 'border-hairline2 bg-inkAlt text-neutral-600'}`} data-id="element-104" data-mp="3m6qwew7nswq">
          {done ? <Check className="h-5 w-5" strokeWidth={2.5} data-id="element-105" data-mp="rlp6fdny234w" /> : <Icon className="h-5 w-5" strokeWidth={2} data-id="element-106" data-mp="15jnmcnhwy7mi" />}
        </div>
      </div>
      <div className={`mt-3 text-sm font-semibold transition-colors duration-500 ${done || current ? 'text-white' : 'text-neutral-500'}`} data-id="element-107" data-mp="11i21ofz50p1r">{stage.label}</div>
      <div className={`mt-0.5 text-[11px] leading-tight transition-colors duration-500 ${done || current ? 'text-neutral-400' : 'text-neutral-600'}`} data-id="element-108" data-mp="37b0ox5wes3fe">{stage.sub}</div>
    </div>;
}

/* Travelling pulse instead of a hard fill (item 5): the track fills with a
   smoother, longer ease, and a small glowing dot travels along it once as it
   fills, then fades. Pure CSS keyframes, no library. */
function FlowConnector({
  filled,
  pulsing
}: {
  filled: boolean;
  pulsing: boolean;
}) {
  return <div className="flex items-center justify-center py-1 md:flex-1 md:px-1 md:py-0" data-id="element-109" data-mp="1nfpzcbpjrnn0">
      <div className="relative h-7 w-px overflow-hidden rounded-full bg-hairline2 md:mt-6 md:h-px md:w-full" data-id="element-110" data-mp="21fr9yivm69fo">
        <div className={`absolute inset-0 origin-top rounded-full bg-gradient-to-b from-emerald-400 to-emerald-500 transition-transform duration-[750ms] ease-[cubic-bezier(0.22,1,0.36,1)] md:origin-left md:bg-gradient-to-r ${filled ? 'scale-y-100 md:scale-x-100' : 'scale-y-0 md:scale-x-0'}`} data-id="element-111" data-mp="1whfzc5xhvuwe" />
        {pulsing && <>
            <span className="va-pulse-dot va-pulse-dot-v md:hidden" aria-hidden="true" data-id="element-112" data-mp="2f9i9qzjjmey7" />
            <span className="va-pulse-dot va-pulse-dot-h hidden md:block" aria-hidden="true" data-id="element-113" data-mp="2ytz9qjsi9mke" />
          </>}
      </div>
    </div>;
}

/* Runs once on entering the viewport instead of looping forever (item 5): an
   IntersectionObserver starts a single staggered play-through the first time the
   diagram scrolls into view, then stops. Reduced motion jumps straight to the
   finished state, same as before. */
function LeadFlow() {
  const [active, setActive] = useState(-1);
  const [pulseIndex, setPulseIndex] = useState(-1);
  const [started, setStarted] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  useEffect(() => {
    const el = containerRef.current;
    if (!el || started) return;
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setStarted(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.35
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    if (reducedMotion) {
      setActive(FLOW_STAGES.length - 1);
      return;
    }
    const timers = FLOW_STAGES.map((_, i) => setTimeout(() => {
      setPulseIndex(i - 1);
      setActive(i);
    }, i * 550));
    const clearPulse = setTimeout(() => setPulseIndex(-1), FLOW_STAGES.length * 550 + 750);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(clearPulse);
    };
  }, [started, reducedMotion]);
  const capturing = active === 0;
  return <div ref={containerRef} className="relative mt-10 overflow-hidden rounded-xl border border-hairline2 bg-inkAlt p-6 sm:p-10" data-id="element-114" data-mp="cmkzvglyjpxn">
      <style data-id="element-115" data-mp="2xd3c43zqcovt">{`
        @keyframes vaPing{0%{transform:scale(1);opacity:.5}70%,100%{transform:scale(2);opacity:0}}
        @keyframes vaPulseV{0%{transform:translateY(-100%);opacity:0}15%{opacity:1}85%{opacity:1}100%{transform:translateY(700%);opacity:0}}
        @keyframes vaPulseH{0%{transform:translateX(-100%);opacity:0}15%{opacity:1}85%{opacity:1}100%{transform:translateX(700%);opacity:0}}
        @keyframes vaGlow{0%,100%{opacity:.4}50%{opacity:.75}}
        .va-pulse-dot{position:absolute;width:10px;height:10px;border-radius:9999px;background:radial-gradient(circle,rgba(52,211,153,.95) 0%,rgba(52,211,153,0) 72%);pointer-events:none}
        .va-pulse-dot-v{left:50%;top:0;margin-left:-5px;animation:vaPulseV 700ms cubic-bezier(0.22,1,0.36,1) both}
        .va-pulse-dot-h{top:50%;left:0;margin-top:-5px;animation:vaPulseH 700ms cubic-bezier(0.22,1,0.36,1) both}
        .va-glow{animation:vaGlow 6s ease-in-out infinite}
        @media (prefers-reduced-motion: reduce){.va-pulse-dot{animation:none;display:none}.va-glow{animation:none}}
      `}</style>
      <div className="va-glow pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(55% 60% at 50% 38%, rgba(15,176,125,0.12) 0%, rgba(10,10,10,0) 72%)'
    }} data-id="element-116" data-mp="f7snui9kfd82" />
      <div className="relative" data-id="element-117" data-mp="3c04xd9a8qjjd">
        <div className="font-eyebrow text-center text-[10px] font-semibold uppercase text-neutral-500" data-id="element-118" data-mp="3c2zgmxhipyb7">Leads come in from</div>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2" data-id="element-119" data-mp="3l1uoee07nggm">
          {FLOW_SOURCES.map(s => <span key={s} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-500 ${capturing ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-hairline2 bg-ink text-neutral-500'}`} data-id="element-120" data-mp="2xb8tlof7vsok">
              <span className={`h-1.5 w-1.5 rounded-full transition-colors duration-500 ${capturing ? 'bg-emerald-400' : 'bg-neutral-600'}`} data-id="element-121" data-mp="32bdahfk1epfb" />{s}
            </span>)}
        </div>
        <div className="mx-auto mt-3 h-5 w-px bg-gradient-to-b from-emerald-500/60 to-transparent" data-id="element-122" data-mp="29mfs7cftssmk" />
        <div className="mt-2 flex flex-col items-center md:flex-row md:items-start md:justify-center" data-id="element-123" data-mp="p4dmpdya2bx4">
          {FLOW_STAGES.map((stage, i) => {
          const state: 'idle' | 'current' | 'done' = active > i ? 'done' : active === i ? 'current' : 'idle';
          return <React.Fragment key={stage.label} data-id="element-124" data-mp="g1mb9e5ejg5h" data-mpi={i}>
                <FlowNode stage={stage} state={state} data-id="element-125" data-mp="k4ith3r15a8w" />
                {i < FLOW_STAGES.length - 1 && <FlowConnector filled={active > i} pulsing={pulseIndex === i} data-id="element-126" data-mp="17udalxidtkyy" />}
              </React.Fragment>;
        })}
        </div>
        <div className="mt-8 text-center text-xs text-neutral-500" data-id="element-127" data-mp="1ct6kzh2dgvuw">One system runs the whole job, start to finish. You just show up and do the work.</div>
      </div>
    </div>;
}

/* THE DARE -- restored 2026-08-18 after Kevin confirmed the receptionist is
   reliable enough to hand to a cold stranger. It was cut earlier the same day
   (the case-study section took its slot) while the hero kept promising it:
   "Call right now and our AI will answer. That's the whole product, live."
   That left the page making a claim it no longer backed.
   Restored verbatim from the version that was actually live (artifact aa891d60,
   v10), NOT from v9 -- v9's test-call pull-quote was deliberately removed in
   v10 and stays removed. Only change: the CTA fires trackCallTapped, not the
   old trackLead, so a dare tap stays honest intent and never counts as a
   converted Lead.

   HIGHEST-VARIANCE ELEMENT ON THE PAGE: if the receptionist sounds sharp this is
   the best asset here; if it drifts robotic on any given day it hands a skeptic
   proof of the exact objection he arrived with. Call the line twice a week and
   treat any drift as a P1. */
function TheDare() {
  return <section className="relative overflow-hidden border-b border-hairline bg-inkAlt" data-id="element-128" data-mp="xem2vujfyt1s">
      <div className="pointer-events-none absolute inset-0 opacity-70" style={{
      background: 'radial-gradient(60% 70% at 50% 50%, rgba(15,176,125,0.14) 0%, rgba(10,10,10,0) 70%)'
    }} data-id="element-129" data-mp="2vrcnowe3i5x4" />
      <div className="relative mx-auto max-w-3xl px-5 py-16 text-center sm:py-24" data-id="element-130" data-mp="dk2x59u50e3v">
        <Eyebrow data-id="element-131" data-mp="ii4e9p8063cp">Don't take our word for it</Eyebrow>
        <h2 className="mt-4 text-3xl font-extrabold leading-[1.15] tracking-tight text-white sm:text-4xl" data-id="element-132" data-mp="igjn89kwd2jl">
          Think AI won't close?<br className="hidden sm:block" data-id="element-133" data-mp="raoir6az725g" /> Call it right now.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-neutral-300" data-id="element-134" data-mp="r9wqo5gl7mjx">
          The number below isn't a sales line. It's the exact system we'd put on your phone,
          answering live. Try to stump it. Ask what it costs. Ask if it's a robot.
        </p>

        <div className="mx-auto mt-8 max-w-md" data-id="element-135" data-mp="3kbzx0e6zl03">
          <a href={PHONE_TEL} onClick={trackCallTapped} className="group flex w-full items-center justify-center gap-3 rounded-md bg-emerald-500 px-7 py-5 text-lg font-extrabold tracking-tight text-black transition-colors hover:bg-emerald-400" data-id="element-136" data-mp="8k6qt3xywal5">
            <PhoneCall className="h-5 w-5 flex-none" data-id="element-137" data-mp="vmt8jopfdi09" />
            Call {PHONE_DISPLAY}
          </a>
          <p className="mt-3 text-xs text-neutral-500" data-id="element-138" data-mp="qnp1b3m29uck">
            Answers in seconds, 24/7, same as it would for your customers.
          </p>
        </div>

        <p className="mx-auto mt-10 max-w-lg text-sm leading-relaxed text-neutral-400" data-id="element-139" data-mp="14gocitet92eb">
          That's what picks up at 9pm when you're covered in drywall dust. Not voicemail.
        </p>
      </div>
    </section>;
}
export function App() {
  return <div className="min-h-full w-full bg-ink text-neutral-100" data-id="element-140" data-mp="19epvlec8l1ps">
      {/* THE DESCENT (Kevin, 2026-08-18: "this is a funnel... it needs to catch
          them again, keep them interested all the way down.") Section order is a
          designed argument, not a stack of cards:
            1  HERO        the promise + zero risk, both CTAs
            2  PAIN        mirror (scenarios) -> evidence (stats) -> escalation (reputation)
            3  FIX         the mechanism: flow diagram, the SMS receipt, features, the 11pm outcome
            4  DARE        skepticism peaks right after mechanism -> hand them the phone
            5  CASES       the dare proves it works live; these prove it pays
            6  WHO         credibility right before the ask
            7  OFFER       price + value stack + the 15,050 math
            8  GUARANTEE   the answer to the fear the price just created
            9  ONBOARDING  the answer to the LAST fear: how much of my time is this
           10  FAQ         the objections that survived the argument, in their words
           11  SAFETY NET  the low-commitment form, catching everyone who won't book
           12  CLOSER      belief + last CTAs
          Reorder only with the whole argument in mind: a section that's right in
          isolation can still be wrong in sequence. The form used to sit at #3,
          asking for contact info before the visitor knew what we do; the
          guarantee used to sit three screens above the price it exists to
          defend. Onboarding is deliberately AFTER the guarantee -- price creates
          the money fear, the guarantee answers it, and only then does "how much
          work is this for me" become the live objection. */}

      {/* 1. HERO -- reverted 2026-08-18 (Kevin's own review, overruling the prior
             "$15,050" hero). His reasoning: this buyer has already heard every
             money-back, cancel-anytime pitch there is, so the first thing on the
             page has to disarm, not impress. Subhead tuned to risk removal, not
             capability. Message-match: per the 2026-08-18 first-hand
             Meta snapshot, "Ad - pain" is the ONLY active ad and matches this H1
             verbatim; stat proof / guarantee / one-system are all paused. Current
             truth lives in ads-team/campaigns/_performance-latest.md + FUNNEL-SPEC,
             never in this comment.
              CTA HIERARCHY FLIPPED 2026-08-18: Call is now the filled primary and
             Book is the outline secondary. Two reasons -- a contractor reaches
             for the phone before a calendar link, and the call IS the demo (the
             number rings the GHL line and the AI answers), so the lower-friction
             action is also the more convincing one. Below the fold, Book stays
             primary on purpose: by then the visitor has read the argument.
             This is the single hero variable under test -- do not stack another
             hero change on top of it or the read is unreadable.
              CLAIM TIGHTENED 2026-08-19 (Kevin): H1 and subhead moved from "every
             lead / within seconds" to "every missed call / 5 minutes", which is
             exactly what the terms guarantee. That closes the OPEN ISSUE the
             guarantee section carried -- a prospect never caught the gap, but a
             client asking for a refund in month one would have. "No fine print,
             no hoops to jump through" is gone for the same reason: there is a
             terms section on this page, so the line was false.
             ACTION REQUIRED OUTSIDE THIS FILE: the active "Ad - pain" Meta ad was
             message-matched to the OLD H1 verbatim. Update the ad headline to the
             new H1 or the match breaks and the landing read gets noisy. */}
      <header className="relative overflow-hidden border-b border-hairline" data-id="element-141" data-mp="1usoxuuvw77zx">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{
        background: 'radial-gradient(60% 50% at 50% 0%, rgba(15,176,125,0.16) 0%, rgba(10,10,10,0) 70%)'
      }} data-id="element-142" data-mp="1g4vhv92wa3gt" />
        <div className="relative mx-auto flex max-w-3xl flex-col items-center px-5 pb-14 pt-10 text-center sm:pb-18 sm:pt-14" data-id="element-143" data-mp="174ywbgbsxkpz">
          <img src={LOGO_URL} alt="Viridia Analytics" className="h-16 w-auto sm:h-24" data-id="element-144" data-mp="1b6tvofa69o14" />
          <div className="mt-8" data-id="element-145" data-mp="x7ap3qx0ekqx"><Eyebrow data-id="element-146" data-mp="x6rj98kmvmt9">Phoenix Home-Service Owners</Eyebrow></div>
          <h1 className="mt-4 text-[2.2rem] font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl" data-id="element-147" data-mp="125vpbqc9h4fd">
            We catch every missed call, or you don't pay.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-400 sm:text-lg" data-id="element-148" data-mp="dkpl7oq5ee4g">
            Your AI receptionist answers calls, texts, and Yelp leads day and night. If a missed call
            sits longer than 5 minutes during your first 14 days, that month is on us.
          </p>

          <div className="mt-8 grid w-full max-w-md grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-149" data-mp="11i52j3sc6ac8">
            <CallCTA full variant="primary" data-id="element-150" data-mp="wjbona950hbj" />
            <BookCTA full variant="secondary" data-id="element-151" data-mp="wia9atl398o0" />
          </div>
          <p className="mt-3 text-xs text-neutral-500" data-id="element-152" data-mp="1b7yfa847svi2">
            Call right now and our AI picks up. That's the whole product, live, before you give us a dollar.
          </p>

          <div className="mt-7" data-id="element-153" data-mp="16bhf2fjqqqom"><GuaranteeBadge big data-id="element-154" data-mp="1k8ms4zhvgptd" /></div>
        </div>
      </header>

      {/* 2. THE PAIN -- mirror first, evidence second (reordered 2026-08-18,
             Kevin's descent pass): the four scenario cards used to sit BELOW the
             stats box. A stranger's statistics don't land until the reader has
             seen himself in the scene, so now it's scenes -> stats -> the
             reputation escalation. */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-155" data-mp="3jfl3qphx29gs">
        <div className="mx-auto max-w-4xl px-5 py-14 sm:py-20" data-id="element-156" data-mp="3oey0q32i4wx3">
          <Eyebrow data-id="element-157" data-mp="3oeefacdn7qeb">What a missed call actually costs you</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-158" data-mp="2qrz23ms91ewk">
            The caller doesn't leave a voicemail. They just call the next name on Google.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-159" data-mp="30p3mpi6g52b9">
            A busy shop misses 1 out of every 4 calls. At a few thousand dollars a job, that's tens of
            thousands walking out the door every month, leads you already paid Google, Yelp, and Meta to send you.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-160" data-mp="jzep0xk885tb">
            {["You're on a rooftop swapping a condenser and the phone buzzes in your pocket.", "You're under a sink mid-repair with wet hands and no way to grab it.", "You're driving between jobs and it goes straight to voicemail.", "You're mid-estimate with one customer while a second lead is calling right now."].map((line, i) => <div key={i} className="rounded-lg border border-hairline bg-ink p-5 text-sm leading-relaxed text-neutral-400" data-id="element-161" data-mp="1lsiw3lzfo2i6" data-mpi={i}>{line}</div>)}
          </div>
          <ProofStats data-id="element-162" data-mp="1crkqx0d9doy0" />

          {/* REPUTATION -- replaced the cost calculator (Kevin, 2026-08-18):
                 "get rid of the calculator... we need to hit pain, reduce risk,
                 sell the outcome. Reputation is marketing, not your cool logo or
                 vehicle wrapping." The calculator was homework; this is the
                 knife. */}
          <div className="mt-10 rounded-xl border border-emerald-500/25 bg-ink p-7 sm:p-9" data-id="element-163" data-mp="327adz4yydrd8">
            <Eyebrow data-id="element-164" data-mp="3l9kqxbi0u659">Reputation is marketing</Eyebrow>
            <h3 className="mt-3 text-xl font-bold leading-snug text-white sm:text-2xl" data-id="element-165" data-mp="3q2dtpttxj4ep">
              A missed call costs you the job. A missed appointment costs you your name.
            </h3>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-166" data-mp="3h9ahfov0sxln">
              Thirty quiet minutes is all it takes. No answer, not even a missed-call text, and that
              $10,000 job is already talking to the next name on the list. You didn't lose it on price
              or on quality. You lost it in the half hour you couldn't reach your phone.
            </p>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-167" data-mp="3udw9qomfhx5">
              And the appointment that got booked and forgotten? That's not a missed job anymore.
              That's a one-star review, doing your marketing for you, to every neighbor who searches
              your name for the next five years.
            </p>
            <p className="mt-5 max-w-2xl text-base font-semibold leading-relaxed text-white" data-id="element-168" data-mp="2gzsnxy8f44fd">
              Your reputation <span className="text-emerald-400" data-id="element-169" data-mp="2c1zk3bul7mi9">is</span> your marketing. Not the logo.
              Not the truck wrap. What homeowners say about you when you're not in the room.
            </p>
          </div>
        </div>
      </section>

      {/* 3. THE FIX */}
      <section className="border-b border-hairline" data-id="element-170" data-mp="1xcl5npc5mew0">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-171" data-mp="1xb0dg6dbb81b">
          <Eyebrow data-id="element-172" data-mp="1td2qzbaxf01m">From missed call to five-star review</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-173" data-mp="273poj04yajw4">
            One lead comes in. The system runs the entire job for you.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-174" data-mp="1emvc15k4nz5o">
            Every call, text, and message from every platform lands in one place, and the system carries
            it the whole way: booked, invoiced, completed, and reviewed. Nothing slips.
          </p>
          <LeadFlow data-id="element-175" data-mp="p4b9wq4o6nuu" />

          {/* THE RECEIPT (2026-08-18): the diagram above is an illustration of a
              claim. This is the claim actually happening -- same minute in, same
              minute out, customer confirming they booked. It sits immediately
              after the diagram so "here is how it works" is followed by "here it
              is working" before the visitor has time to file the diagram under
              marketing. */}
          <div className="mt-8" data-id="element-176" data-mp="1c13gl9pagoi3">
            <ProofShot src={SHOT_SMS} label="AI reply + customer confirming the booking" alt="The AI answering a landscaping lead and the customer replying that they scheduled" caption="A real lead, answered automatically, booked without anyone picking up a phone. Look at the timestamps: the reply goes out the same minute the lead came in." data-id="element-177" data-mp="1c01tg6v00bk0" />
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-178" data-mp="3g8z9zb6fl06h">
            {[{
            icon: Bot,
            title: 'Instant response to every lead',
            body: 'An AI receptionist answers every call, text, and message in seconds, 24/7, before they dial the next name.'
          }, {
            icon: Inbox,
            title: 'One unified inbox',
            body: 'Every call, text, Yelp, Google, and Facebook message lands in one place, not five apps.'
          }, {
            icon: LayoutGrid,
            title: 'Branded CRM + mobile app',
            body: 'Run the whole thing from your phone. It chases every lead until they book or say no.'
          }, {
            icon: Star,
            title: 'Reviews + QuickBooks-synced invoicing',
            body: 'Reputation and invoicing run in the background while you run the job. Google Business Profile set up and optimized.'
          }].map(({
            icon: Icon,
            title,
            body
          }, i) => <div key={i} className="rounded-lg border border-hairline2 bg-inkAlt p-6" data-id="element-179" data-mp="llcbyp2q67s1" data-mpi={i}>
                <Icon className="h-5 w-5 text-emerald-500" strokeWidth={2} data-id="element-180" data-mp="grzx36nulfch" />
                <h3 className="mt-4 text-base font-semibold text-white" data-id="element-181" data-mp="27f806m9reg0d">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-182" data-mp="22eskho208wf8">{body}</p>
              </div>)}
          </div>

          {/* THE 11PM STORY (Kevin, 2026-08-18): "our clients are waking up to
                 appointments in their calendar that got booked at 11pm." Sell the
                 outcome, not the feature list. Paired 2026-08-18 with the contact
                 list screenshot, whose Created column carries real 11:06pm /
                 10:54pm / 9:27am timestamps -- the story and its receipt side by
                 side, so it reads as evidence instead of a nice anecdote. */}
          <div className="mt-10 grid grid-cols-1 items-start gap-6 lg:grid-cols-2" data-id="element-183" data-mp="2ccgicmuwqk1m">
            <div className="rounded-xl border border-hairline2 bg-inkAlt p-6 sm:p-8" data-id="element-184" data-mp="39toylnaatska">
              <Eyebrow data-id="element-185" data-mp="351dqhx5en0gn">What that looks like on a Tuesday night</Eyebrow>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-300" data-id="element-186" data-mp="3cd1sak0qwbdy">
                <span className="font-semibold text-white" data-id="element-187" data-mp="3cfej7mohdw35">11:04pm.</span> A homeowner's water heater lets
                go. They call the first three names on Google. Two ring to voicemail.
                <span className="font-semibold text-white" data-id="element-188" data-mp="3mdc5w5qozktn"> Yours answers on the second ring</span>, walks
                them through it, and books the estimate. <span className="font-semibold text-white" data-id="element-189" data-mp="3vcphb3ydmjh0">11:07pm,
                it's on your calendar.</span> You find out at breakfast.
              </p>
              <p className="mt-4 max-w-2xl text-base font-semibold leading-relaxed text-white" data-id="element-190" data-mp="43jt38iyxjsk">
                Our clients wake up to jobs that booked themselves overnight. No call-back list. No
                shooting from the hip. That's the whole point.
              </p>
            </div>
            <ProofShot src={SHOT_LEADS} label="contact list with after-hours timestamps" alt="A client's lead list showing leads captured late at night and early in the morning" caption="One client's actual lead list. Check the Created column: 11:06pm, 10:54pm, 9:27am. Every one of them answered while the owner was asleep or on a job." data-id="element-191" data-mp="huybbd51xgyk" />
          </div>

          <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-192" data-mp="2nyby64ebxemx">
            <BookCTA data-id="element-193" data-mp="32n89cabon7rr" />
            <CallCTA data-id="element-194" data-mp="32mgmpp02ckdm" />
          </div>
        </div>
      </section>

      {/* AD OPTIMIZATION SECTION REMOVED 2026-08-18 (Kevin: "Get rid of the Ad
          optimization, I can do that upsell on the call. or after the 14 days.").
          It promised ad tuning three screens above a $1,197 price that does not
          include ad management, so a client could quote it back at us in month two.
          It is an upsell now, sold on the call or after the 14-day window -- not a
          claim on the landing page. Do not reinstate it here without also changing
          what the $1,197 covers.
          Known trade-off: this section also qualified out the shopper looking for a
          $100 answering service. If cheap-shopper calls start eating the calendar,
          solve it in the ad or the booking form questions, not by putting an
          unincluded service back on the page. */}

      {/* 4. THE DARE -- sits right after the mechanism, where skepticism peaks:
             the visitor has just been told what the system does, so this is the
             moment to hand them the phone instead of arguing. */}
      <TheDare data-id="element-195" data-mp="3a7k98adln6w7" />

      {/* 5. CASE STUDIES -- the dare proves the product works live, these prove
             it produced an outcome. Both, not either. */}
      <CaseStudies data-id="element-196" data-mp="308bidqe168e2" />

      {/* 6. WHO'S BEHIND IT -- credibility right before the ask (moved above the
             offer in the 2026-08-18 descent pass). Trimmed earlier the same day:
             the two client results live in the case-study section as full
             stories, so this carries the credibility line, trust badge, and CTA
             without repeating the numbers. */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-197" data-mp="4rhd0blzwn2d">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-198" data-mp="4s8ygtlvp4ex">
          <Eyebrow data-id="element-199" data-mp="3vwqybpz1od5n">Who's behind this</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-200" data-mp="3vzv95670m75a">
            Run by people who've done this before, not a call center script.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-201" data-mp="3myezx1rxsb2n">
            Built by former Yelp reps who managed over $2 million a month in ad spend for home-service businesses.
          </p>

          <div className="mt-10 flex flex-col items-center gap-4" data-id="element-202" data-mp="3d4y4xug2cpxe">
            <div className="flex flex-col gap-3 sm:flex-row" data-id="element-203" data-mp="389iw5gwwxk7u">
              <BookCTA data-id="element-204" data-mp="339mp18sx5co2" />
              <CallCTA data-id="element-205" data-mp="3381wfoq603su" />
            </div>
            <div className="flex flex-wrap items-center gap-6 opacity-90" data-id="element-206" data-mp="33axst8md6kf6">
              <img src={CHAMBER_BADGE} alt="Scottsdale Chamber of Commerce" className="h-9 w-auto" data-id="element-207" data-mp="2z81dprw196fz" />
            </div>
          </div>
        </div>
      </section>

      {/* 7. OFFER -- item 8's promotional-rate change was cancelled by Kevin the
             same day (commit f072d19, "dont do the 897 thing") before this run
             read the handoff. Pricing stays exactly as it was: plain $1,197/month,
             no strikethrough, no founding-rate framing, no scarcity language
             attached to price. The 5-businesses-a-month line stays a capacity
             statement further down, not a pricing deadline. */}
      <section className="border-b border-hairline" data-id="element-208" data-mp="14znvbhctp67i">
        <div className="mx-auto max-w-2xl px-5 py-14 text-center sm:py-20" data-id="element-209" data-mp="14yvzq6j878h8">
          <Eyebrow data-id="element-210" data-mp="19qnr2ja7hi8f">The offer</Eyebrow>
          <h2 className="mt-3 text-4xl font-extrabold text-white sm:text-5xl" data-id="element-211" data-mp="19rp2iov4w6qo">$1,197<span className="text-xl font-semibold text-neutral-400" data-id="element-212" data-mp="c9yv367xta6y">/month</span></h2>
          <p className="mt-2 text-sm font-medium text-emerald-500" data-id="element-213" data-mp="h9bf7j7fc35u">Month to month. No contract. Cancel anytime.</p>

          <div className="mx-auto mt-7 max-w-md rounded-xl border border-hairline2 bg-inkAlt p-6 text-left" data-id="element-214" data-mp="98c6zrlmfe6w">
            <p className="text-center text-sm font-semibold text-white" data-id="element-215" data-mp="57af5vu1a4ey">Why that number is a steal:</p>
            <ul className="mt-4 space-y-2.5" data-id="element-216" data-mp="iyp0487i4fon">
              {['Replaces a full-time receptionist ($3,000+/mo). The AI answers every call, 24/7.', 'Replaces your CRM, review tool, and invoicing. One bill instead of five apps.', 'Catches the calls you already paid Google, Yelp & Meta to send you.', 'Built and set up with you, in your voice. Live in days, not months.'].map(li => <li key={li} className="flex items-start gap-2 text-sm leading-relaxed text-neutral-300" data-id="element-217" data-mp="3reqscbm7yqfb">
                  <Check className="mt-0.5 h-4 w-4 flex-none text-emerald-500" strokeWidth={2.5} data-id="element-218" data-mp="1bmt66utziurv" />{li}
                </li>)}
            </ul>
            <p className="mt-4 border-t border-hairline2 pt-4 text-center text-sm font-bold text-emerald-400" data-id="element-219" data-mp="16r5eo8s15pj7">
              One client turned $1,197 into $15,050 in thirty days, from estimates the AI booked on its
              own. One missed $3,000 job costs more than a month of this. Why would you let that go?
            </p>
          </div>

          <div className="mt-6" data-id="element-220" data-mp="2klu9okgmd3gg"><GuaranteeBadge data-id="element-221" data-mp="2klcg5vlcd3c8" /></div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-500" data-id="element-222" data-mp="38adj53benma4">
            <span className="inline-flex items-center gap-1.5" data-id="element-223" data-mp="3cciatwiam8u4"><Clock className="h-3.5 w-3.5 text-emerald-500" data-id="element-224" data-mp="3cb771woul130" /> Live in {DAYS_TO_LIVE} days</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-225" data-mp="2ykbv2bj1y3v7"><Users className="h-3.5 w-3.5 text-emerald-500" data-id="element-226" data-mp="33j59q7a2s0wy" /> Only 5 new businesses onboarded per month</span>
          </div>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-227" data-mp="3r87ydz34mu0y">
            <BookCTA data-id="element-228" data-mp="3w113wp6f66e3" />
            <CallCTA data-id="element-229" data-mp="3w1albodef586" />
          </div>
        </div>
      </section>

      {/* 8. GUARANTEE -- deliberately AFTER the offer (2026-08-18 descent pass):
             the price creates the fear, this answers it on the very next scroll.
             It used to sit three screens above the price, where it defended
             nothing. Names the mechanisms explicitly; scope (response speed
             only, timestamped and logged) and the delivery carve-out unchanged.
              RESOLVED 2026-08-19 (Kevin): the old seconds-vs-5-minutes and every-
             lead-vs-missed-calls mismatch was closed by tightening the HERO down
             to the terms, not by widening the terms. The page now makes one claim
             in one wording -- keep it that way on any future copy pass. */}
      <section className="border-b border-hairline" data-id="element-230" data-mp="20w9tw2fix8r0">
        <div className="mx-auto max-w-3xl px-5 py-14 text-center sm:py-20" data-id="element-231" data-mp="tizsw418jplp">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10" data-id="element-232" data-mp="tgwryoyjoc27">
            <ShieldCheck className="h-7 w-7 text-emerald-500" data-id="element-233" data-mp="315stv5wjm5it" />
          </div>
          <h2 className="mt-5 text-2xl font-extrabold leading-snug text-white sm:text-3xl" data-id="element-234" data-mp="356bk4visf3iw">
            We respond to every missed call in your first 14 days, or your first month is 100% money back.
          </h2>
          <div className="mx-auto mt-6 grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-235" data-mp="3ewo4onqlqwp1">
            {[{
            icon: Zap,
            text: 'Auto-responders fire for every lead'
          }, {
            icon: Bot,
            text: 'AI receptionist configured for missed calls'
          }, {
            icon: MessageSquare,
            text: 'Missed-call text-back'
          }].map(({
            icon: Icon,
            text
          }) => <div key={text} className="flex items-center gap-2.5 rounded-lg border border-hairline2 bg-inkAlt p-4 sm:flex-col sm:items-center sm:gap-2 sm:text-center" data-id="element-236" data-mp="1pmdl9j7ftt8">
                <Icon className="h-4 w-4 flex-none text-emerald-500 sm:h-5 sm:w-5" strokeWidth={2} data-id="element-237" data-mp="1yk92tcda584e" />
                <span className="text-xs font-medium leading-snug text-neutral-300" data-id="element-238" data-mp="23jbxq3zjpxyn">{text}</span>
              </div>)}
          </div>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-neutral-400" data-id="element-239" data-mp="1tu12w4ziljlu">
            We guarantee the one thing we fully control: missed-call response speed. Every response is
            timestamped and logged. If our system does not respond to every missed call within 5 minutes
            during your first 14 days, your first month is refunded, no hassle. See full terms below.
          </p>
        </div>
      </section>

      {/* 9. ONBOARDING -- new. See the component for the design rules that keep
             it working: their column stays empty, no features, every step has a
             time on it. */}
      <Onboarding data-id="element-240" data-mp="22suwrnlgnvvp" />

      {/* 10. FAQ -- last stop before the low-commitment form. See the component
             comment for the editing rules; the short version is that it frames
             the terms and never contradicts them. */}
      <FAQ data-id="element-241" data-mp="jvrlchaz2q9x" />

      {/* 11. THE SAFETY NET -- the low-commitment door, moved from slot 3 in the
             2026-08-18 descent pass. Catches everyone the offer + guarantee
             convinced but didn't push to book or dial. */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-242" data-mp="tu7dmnlqk5l0">
        <div className="mx-auto max-w-xl px-5 py-16 sm:py-24" data-id="element-243" data-mp="ouurk52ov2jh">
          <SendNumbersForm data-id="element-244" data-mp="ovdx0mb5jygm" />
        </div>
      </section>

      {/* 12. CLOSER */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-245" data-mp="161rbwf1a8j2">
        <div className="mx-auto max-w-2xl px-5 py-20 text-center sm:py-28" data-id="element-246" data-mp="3sbtgash2rf4a">
          <h2 className="text-3xl font-extrabold leading-tight text-white sm:text-4xl" data-id="element-247" data-mp="3s9qdt8yqjtio">You're doing everything right. Stop letting the phone undo it.</h2>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-neutral-400" data-id="element-248" data-mp="63bm7rl6zcea">
            You run the ads. You run the crew. You do the work. The only leak in the whole operation
            is the thirty minutes nobody could answer, and that's the one thing we fix completely.
            From first ring to five-star review, covered. Book fifteen minutes, or call right now and
            try to stump it.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-249" data-mp="73bwzrf0xn1e">
            <BookCTA data-id="element-250" data-mp="by81vs5d04vx" />
            <CallCTA data-id="element-251" data-mp="gv85zxinexke" />
          </div>
          <div className="mt-6 flex justify-center" data-id="element-252" data-mp="gu6l71btywph"><GuaranteeBadge data-id="element-253" data-mp="gxc767p5xigz" /></div>
        </div>
      </section>

      {/* TERMS -- rewritten 2026-08-19 (Kevin: "go through that fine print... it
             is too much to me"). Same protections, roughly 40% shorter, plain
             language instead of contract voice. The seven-item delivery carve-out
             list is compressed into one sentence without losing coverage: opt-outs
             and blocks, plus anything a carrier or platform blocks, filters, or
             delays, still fall outside the guarantee. If a protection needs to
             come back, add it as plain language -- do not restore the old
             paragraph-length clauses. */}
      <section id="terms" className="border-b border-hairline" data-id="element-254" data-mp="2u0mq9k048hsl">
        <div className="mx-auto max-w-3xl px-5 py-12" data-id="element-255" data-mp="2p2bev4u69z89">
          <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-256" data-mp="2p4xwxyeovx4q">Guarantee terms &amp; conditions</div>
          <div className="mt-3 space-y-2 text-[11px] leading-relaxed text-neutral-500" data-id="element-257" data-mp="2fdbcdbggolpb">
            <p data-id="element-258" data-mp="2bbgcmbe6igdn">The guarantee, in plain English: during your first 14 days after go-live, our system responds to every missed call within 5 minutes. Every response is timestamped and logged, and those logs are the record. If we miss one, your first month is refunded. One claim per client, and any one-time setup fee is not included in the refund.</p>
            <p data-id="element-259" data-mp="1wfh6v7ejksbo">What it does not cover: booked jobs, revenue, or close rate. Those depend on your pricing, your sales, and your market, and nobody can honestly guarantee them. It also does not apply if the system is disconnected, paused, or bypassed, or if onboarding was not completed — your calendar synced and carrier text registration approved.</p>
            <p data-id="element-260" data-mp="7mlc3gdz8od2">Out of our hands: leads who opted out or blocked the number, and messages a carrier or platform blocks, filters, or delays, do not count against the guarantee.</p>
            <p data-id="element-261" data-mp="3owt6btd6x2b4">Service is month to month; cancel anytime with 7 days' notice. The system runs on your phone carrier, Yelp, Google, and Meta staying connected, and we are not responsible for their outages or policy changes. Client results shown are real past-client outcomes, not a promise of the same results for every business.</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-5 py-10" data-id="element-262" data-mp="3624gyf1x90h2">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center" data-id="element-263" data-mp="3b2sgxq84ornf">
          <img src={LOGO_URL} alt="Viridia Analytics" className="h-12 w-auto opacity-80" data-id="element-264" data-mp="2x3stglwcqisu" />
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-500" data-id="element-265" data-mp="3154k8uqypa2m">
            <a href={PHONE_TEL} onClick={trackCallTapped} className="inline-flex items-center gap-1.5 text-emerald-300 hover:text-emerald-200" data-id="element-266" data-mp="2igkfxefkm1dp"><Phone className="h-3.5 w-3.5" data-id="element-267" data-mp="2dj2d85s5cie5" /> {PHONE_DISPLAY}</a>
            <span className="inline-flex items-center gap-1.5" data-id="element-268" data-mp="2rha7g5xst5lm"><Mail className="h-3.5 w-3.5" data-id="element-269" data-mp="2rj3cjgsrjhri" /> info@viridiaanalytics.com</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-270" data-mp="2mhdz5ui2is09"><MapPin className="h-3.5 w-3.5" data-id="element-271" data-mp="2mks3376dr99t" /> Phoenix, AZ</span>
          </div>
          <a href="https://viridiaanalytics.com" target="_blank" rel="noopener noreferrer" className="text-xs text-neutral-600 underline decoration-neutral-800 underline-offset-4 hover:text-neutral-400" data-id="element-272" data-mp="31dvdmulk0mr1">See the full Viridia Analytics site</a>
        </div>
      </footer>
    </div>;
}